import { Link } from 'react-router-dom';

/**
 * Site-wide footer. Renders on every route via <AppShell />, and also
 * on the standalone Home page (which doesn't use AppShell).
 *
 * Four-column layout: Alhudah brand + disclaimer on the left, three
 * link columns (Read / Study / More) on the right. Stays in the
 * night-* palette across both themes so the brand identity reads
 * consistently regardless of the page background above it.
 */

const SHELL = 'mx-auto w-full max-w-7xl px-5 sm:px-8';

type FooterLink = { to: string; label: string };
type FooterColumn = { heading: string; links: FooterLink[] };

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Read',
    links: [
      { to: '/surah', label: 'All surahs' },
      { to: '/reader', label: 'Focused Reader' },
      { to: '/search', label: 'Search' },
      { to: '/ask', label: 'Ask Alhudah' },
    ],
  },
  {
    heading: 'Study',
    links: [
      { to: '/memorize', label: 'Memorize' },
      { to: '/bookmarks', label: 'Bookmarks' },
      { to: '/qari', label: 'Qari program' },
      { to: '/settings', label: 'Settings' },
    ],
  },
  {
    heading: 'More',
    links: [
      { to: '/blog', label: 'Blog' },
      { to: '/streak', label: 'Streak' },
      { to: '/find-qari', label: 'Find a Qari' },
    ],
  },
];

export function Footer() {
  return (
    <footer className="border-t border-night-500/20 bg-night-950">
      <div className={SHELL + ' grid grid-cols-1 gap-12 py-16 md:grid-cols-12'}>
        <div className="md:col-span-6">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-[1.35rem] bg-white/95 shadow-[0_10px_30px_rgba(15,23,42,0.2)] ring-1 ring-white/60">
              <img
                src="/favicon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain"
              />
            </span>
            <img
              src="/logo/alhudah-main-logo.png"
              alt="Alhudah"
              className="h-7 w-auto sm:h-8"
            />
          </div>
          <p className="mt-5 max-w-sm leading-relaxed text-night-200">
            A calm, modern place to read, understand, and remember the Quran.
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-night-300">
            Alhudah is a reading companion, not a substitute for scholarly
            study. Quranic text is rendered verbatim from the source database
            and is never modified.
          </p>
        </div>

        {FOOTER_COLUMNS.map((col) => (
          <div key={col.heading} className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-night-300">
              {col.heading}
            </h3>
            <ul className="mt-5 space-y-3">
              {col.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-night-200 transition-colors hover:text-beam-pale"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-night-500/20">
        <div
          className={SHELL + ' flex flex-col gap-2 py-6 text-xs text-night-300 sm:flex-row sm:items-center sm:justify-between'}
        >
          <p>&copy; {new Date().getFullYear()} Alhudah</p>
        </div>
      </div>
    </footer>
  );
}
